import { supabase } from '../config/supabase';
import { EventSeries, RecurrenceChangePreview, RecurrenceChangeResult } from '../types/eventSeries.types';
import type { Tables, TablesInsert, TablesUpdate } from '../types/supabase.generated';
import type { Json } from '../types/supabase.generated';
import { smartListenerService } from './SmartListenerService';
import { localToUTC, localEndTimeToUTC } from '../utils/timezoneUtils';

// localEndTimeToUTC est maintenant importé depuis '../utils/timezoneUtils'

// Day name lookup for day_schedules
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
function getDayNameFromDate(date: Date): string {
  return DAY_NAMES[date.getDay()];
}

// Types Supabase
type DbEventSeriesRow = Tables<'event_series'>;
type DbEventSeriesUpdate = TablesUpdate<'event_series'>;
type DbEventInsert = TablesInsert<'events'>;

// Interface pour les prix
interface PriceItem {
  amount: number;
  description: string;
  currency: string;
}

/**
 * Convertit un DbEventSeriesRow (Supabase) en EventSeries (App)
 */
function dbSeriesToEventSeries(row: DbEventSeriesRow): EventSeries {
  return {
    id: row.id,
    organizerId: row.organizer_id,
    timezone: row.timezone ?? 'Asia/Colombo',
    title: row.title,
    description: row.description ?? undefined,
    location: row.location ?? undefined,
    locationName: row.location_name ?? undefined,
    latitude: row.latitude ?? undefined,
    longitude: row.longitude ?? undefined,
    tags: row.tags ?? [],
    image: row.image ?? undefined,
    prices: (row.prices as unknown as PriceItem[]) ?? [],
    ticketingUrl: row.ticketing_url ?? undefined,
    status: (row.status as 'active' | 'draft' | 'cancelled') ?? 'draft',
    cancelledAt: (row as any).cancelled_at ? new Date((row as any).cancelled_at) : undefined,
    cancellationReason: (row as any).cancellation_reason ?? undefined,
    frequency: row.frequency as 'daily' | 'weekly' | undefined,
    startDate: row.start_date ?? undefined,
    startTime: row.start_time ?? undefined,
    endTime: row.end_time ?? undefined,
    endType: row.end_type as 'date' | 'occurrences' | undefined,
    endDate: row.end_date ?? undefined,
    occurrences: row.occurrences ?? undefined,
    weekDays: row.week_days ?? [],
    daySchedules: (row as any).day_schedules ?? null,
    createdAt: row.created_at ? new Date(row.created_at) : new Date(),
    updatedAt: row.updated_at ? new Date(row.updated_at) : new Date(),
  };
}

/**
 * Service Supabase pour la gestion des séries d'événements
 */
export const supabaseEventSeriesService = {
  /**
   * Crée une nouvelle série d'événements
   */
  async createEventSeries(seriesData: Omit<EventSeries, 'id'>): Promise<string> {
    try {
      console.log('🆕 [Supabase] Création série:', seriesData.title);

      const isPublishing = seriesData.status === 'active';

      // Validation de base
      if (!seriesData.frequency || !seriesData.startDate || !seriesData.startTime) {
        throw new Error('Missing required fields for recurring event');
      }

      // Validation pour publication
      if (isPublishing) {
        if (seriesData.endType === 'occurrences' && !seriesData.occurrences) {
          throw new Error('Number of occurrences required when endType is occurrences');
        }
        if (seriesData.endType === 'date' && !seriesData.endDate) {
          throw new Error('End date required when endType is date');
        }
      }

      // Construire les données pour Supabase
      // Note: || null gère les chaînes vides "" en plus de null/undefined
      const insertData: any = {
        organizer_id: seriesData.organizerId,
        title: seriesData.title,
        description: seriesData.description || null,
        location: seriesData.location || null,
        location_name: seriesData.locationName || null,
        latitude: seriesData.latitude ?? null,
        longitude: seriesData.longitude ?? null,
        geohash: seriesData.geohash || null, // Geohash calculé côté client (backup: trigger Supabase)
        timezone: seriesData.timezone || 'Asia/Colombo', // 🌍 Timezone de l'événement
        tags: seriesData.tags ?? [],
        image: seriesData.image || null,
        prices: (seriesData.prices ?? []) as unknown as Json,
        ticketing_url: seriesData.ticketingUrl || null,
        status: seriesData.status ?? 'draft',
        frequency: seriesData.frequency || null,
        start_date: seriesData.startDate || null,
        start_time: seriesData.startTime || null,
        end_time: seriesData.endTime || null,
        end_type: seriesData.endType || null,
        end_date: seriesData.endDate || null,
        occurrences: seriesData.occurrences ?? null,
        week_days: seriesData.weekDays ?? [],
        ...(seriesData.importConfidence != null && { import_confidence: seriesData.importConfidence }),
        ...(seriesData.importSource && { import_source: seriesData.importSource }),
      };

      // day_schedules is not yet in generated types, add separately
      if (seriesData.daySchedules) {
        insertData.day_schedules = seriesData.daySchedules;
      }

      console.log('📤 [Supabase] Données à insérer dans event_series:', JSON.stringify(insertData, null, 2));

      // DEBUG: Vérifier l'état de l'authentification
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      console.log('🔐 [Supabase] État auth:', {
        userId: authUser?.id,
        email: authUser?.email,
        authError: authError?.message,
        organizerIdToInsert: insertData.organizer_id,
        idsMatch: authUser?.id === insertData.organizer_id
      });

      if (!authUser) {
        throw new Error('Utilisateur non authentifié - impossible de créer la série');
      }

      if (authUser.id !== insertData.organizer_id) {
        console.warn('⚠️ [Supabase] ATTENTION: auth.uid() !== organizer_id !');
        console.warn('   auth.uid():', authUser.id);
        console.warn('   organizer_id:', insertData.organizer_id);
      }

      console.log('⏳ [Supabase] Envoi de l\'insert...');

      // 🐢 FIX (robustesse réseau faible - item 5a): on n'utilise plus de course
      // Promise.race entre l'insert et un timeout manuel de 10s. Sur un réseau lent,
      // ce timeout pouvait gagner AVANT que l'insert (déjà committé côté serveur) ne
      // réponde: le code croyait alors à un échec et relançait un 2e insert (fallback
      // "sans select") → DEUX séries créées pour une seule action utilisateur, dont une
      // "fantôme" potentiellement incomplète. On laisse simplement l'appel réseau
      // réussir ou échouer naturellement: un vrai échec réseau throw de lui-même via le
      // client Supabase (fetch), sans qu'on ait besoin de dupliquer la requête d'insert.
      const { data, error } = await supabase
        .from('event_series')
        .insert(insertData)
        .select('id')
        .single();

      console.log('📥 [Supabase] Réponse insert:', { data, error });

      if (error) {
        console.error('❌ [Supabase] Erreur insert event_series:', error);
        throw error;
      }
      if (!data) throw new Error('No data returned from insert');

      const seriesId = data.id;

      // Générer les événements (draft ou active - publish_series RPC passera les drafts en active)
      console.log('🔄 [Supabase] Génération des événements de la série...');
      await this._generateSeriesEvents(seriesId, { ...seriesData, id: seriesId } as EventSeries);

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      await smartListenerService.forceRefreshOrganizerEvents(seriesData.organizerId);
      await smartListenerService.forceRefreshSeries(seriesData.organizerId);

      console.log('✅ [Supabase] Série et événements créés:', seriesId);
      return seriesId;
    } catch (error) {
      console.error('❌ [Supabase] Erreur création série:', error);
      throw error;
    }
  },

  /**
   * Génère les dates pour tous les événements de la série
   */
  _generateEventDates(series: EventSeries): Date[] {
    console.log('📅 [Supabase] Génération des dates:', {
      frequency: series.frequency,
      startDate: series.startDate,
      endType: series.endType,
      occurrences: series.occurrences,
    });

    const dates: Date[] = [];
    if (!series.startDate || !series.startTime) return dates;

    const startDate = new Date(series.startDate + 'T' + series.startTime);

    if (series.frequency === 'daily') {
      let currentDate = new Date(startDate);
      const endDate = series.endType === 'date' && series.endDate
        ? new Date(series.endDate + 'T' + series.startTime)
        : null;

      let count = 0;
      // La limite max dépend du mode:
      // - Si endType === 'date', on utilise 365 comme sécurité (l'endDate contrôle la fin)
      // - Si endType === 'occurrences', on utilise series.occurrences
      const maxOccurrences = series.endType === 'date' ? 365 : (series.occurrences ?? 365);

      while (
        (endDate && currentDate <= endDate) ||
        (!endDate && series.occurrences && count < series.occurrences)
      ) {
        if (count >= maxOccurrences) break;
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
        count++;
      }
    } else if (series.frequency === 'weekly' && series.weekDays?.length) {
      const dayMap: Record<string, number> = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };
      const weekDayIndices = series.weekDays.map(day => dayMap[day.toLowerCase()] ?? -1).filter(d => d >= 0);

      let currentDate = new Date(startDate);
      const endDate = series.endType === 'date' && series.endDate
        ? new Date(series.endDate + 'T' + series.startTime)
        : null;

      // FIX: Pour "After a number of weeks", multiplier le nombre de semaines par le nombre de jours sélectionnés
      // Ex: 2 semaines × 2 jours (Mon, Wed) = 4 occurrences au total
      const totalOccurrences = series.occurrences && weekDayIndices.length > 0
        ? series.occurrences * weekDayIndices.length
        : series.occurrences;

      let count = 0;
      const maxIterations = 365 * 2; // Limite de sécurité (2 ans de jours)
      let iterations = 0;

      while (
        ((endDate && currentDate <= endDate) ||
        (!endDate && totalOccurrences && count < totalOccurrences)) &&
        iterations < maxIterations
      ) {
        if (weekDayIndices.includes(currentDate.getDay())) {
          dates.push(new Date(currentDate));
          count++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
        iterations++;
      }
    }

    console.log(`📅 [Supabase] ${dates.length} dates générées`);
    return dates;
  },

  /**
   * Génère et sauvegarde tous les événements d'une série
   */
  async _generateSeriesEvents(seriesId: string, series: EventSeries): Promise<void> {
    try {
      console.log('🔄 [Supabase] Génération événements pour série:', seriesId);

      const dates = this._generateEventDates(series);
      if (dates.length === 0) {
        console.log('⚠️ [Supabase] Aucune date générée');
        return;
      }

      // 🐢 FIX (robustesse réseau faible - item 5b): la génération doit être REPRENABLE.
      // Avant: dès qu'un seul événement existait pour la série, on arrêtait tout
      // ("if (existingEvents.length > 0) return"). Or l'insertion se fait par lots de 50
      // — si un lot échoue en cours de route (coupure réseau) puis que l'utilisateur
      // relance (retry, ou un nouveau clic sur "Publier"), cette garde faisait que la
      // reprise ne faisait RIEN: la série restait incomplète en silence ("il manque des
      // mardis"). On compare maintenant les dates attendues (pattern) aux dates déjà
      // présentes en base, et on n'insère QUE les occurrences manquantes.
      const { data: existingEvents, error: checkError } = await supabase
        .from('events')
        .select('id, datetime')
        .eq('series_id', seriesId);

      if (checkError) throw checkError;

      const existingDatesSet = new Set(
        (existingEvents ?? []).map(e =>
          e.datetime ? new Date(e.datetime).toISOString().split('T')[0] : ''
        )
      );

      const missingDates = dates.filter(
        d => !existingDatesSet.has(d.toISOString().split('T')[0])
      );

      if (missingDates.length === 0) {
        console.log(`✅ [Supabase] ${existingEvents?.length ?? 0} événement(s) déjà présent(s) pour cette série, rien à générer (reprise idempotente)`);
        return;
      }

      if (existingEvents && existingEvents.length > 0) {
        console.log(`🔄 [Supabase] Reprise détectée: ${existingEvents.length} événement(s) déjà présent(s), ${missingDates.length} occurrence(s) manquante(s) à compléter sur ${dates.length} attendue(s)`);
      }

      // Créer les événements manquants en batch
      // Note: || null gère les chaînes vides "" en plus de null/undefined
      const timezone = series.timezone || 'Asia/Colombo';
      const events: DbEventInsert[] = missingDates.map(date => {
        // Extraire la date au format YYYY-MM-DD pour la conversion UTC
        const dateStr = date.toISOString().split('T')[0];
        // Use per-day schedule if available, otherwise fall back to uniform time
        const dayName = getDayNameFromDate(date);
        const daySchedule = series.daySchedules?.[dayName];
        const startTime = daySchedule?.start || series.startTime || '00:00';
        const endTime = daySchedule?.end || series.endTime;
        // Convertir l'heure locale de l'événement en UTC avec le bon timezone
        const datetimeUTC = localToUTC(dateStr, startTime, timezone);
        const endTimeISO = localEndTimeToUTC(dateStr, startTime, endTime, timezone, datetimeUTC);

        return {
          organizer_id: series.organizerId,
          series_id: seriesId,
          title: series.title,
          description: series.description || null,
          datetime: datetimeUTC,
          end_time: endTimeISO || null,
          timezone: timezone, // 🌍 Timezone de l'événement
          location: series.location || null,
          location_name: series.locationName || null,
          latitude: series.latitude ?? null,
          longitude: series.longitude ?? null,
          tags: series.tags ?? [],
          image: series.image || null,
          prices: (series.prices ?? []) as unknown as Json,
          ticketing_url: series.ticketingUrl || null,
          status: (series.status || 'active') as 'active' | 'draft',
          is_exception: false,
          week_days: series.weekDays ?? [],
          series_frequency: series.frequency || null,
          series_end_type: series.endType || null,
          series_end_date: series.endDate || null,
          series_occurrences: series.occurrences ?? null,
        };
      });

      // Insérer par lots de 50
      const batchSize = 50;
      for (let i = 0; i < events.length; i += batchSize) {
        const batch = events.slice(i, i + batchSize);
        const { error } = await supabase.from('events').insert(batch);
        if (error) throw error;
        console.log(`✅ [Supabase] Lot ${Math.floor(i / batchSize) + 1} inséré (${batch.length} événements)`);
      }

      // Notifier l'UI (eventsCount = total attendu pour la série, pas seulement le lot inséré ici)
      window.dispatchEvent(new CustomEvent('eventCreated', {
        detail: {
          seriesId,
          organizerId: series.organizerId,
          status: 'active',
          type: 'series',
          eventsCount: dates.length
        }
      }));

      console.log(`✅ [Supabase] ${events.length} occurrence(s) manquante(s) générée(s) pour la série (total attendu: ${dates.length})`);
    } catch (error) {
      console.error('❌ [Supabase] Erreur génération événements:', error);
      throw error;
    }
  },

  /**
   * Met à jour une série et ses événements
   * Gère automatiquement les changements de récurrence (suppression/création d'occurrences)
   */
  async updateEventSeries(
    seriesId: string,
    updates: Partial<EventSeries>,
    options: { skipRecurrenceSync?: boolean } = {}
  ): Promise<RecurrenceChangeResult | void> {
    try {
      console.log('📝 [Supabase] Mise à jour série:', seriesId, updates);

      // 0. Récupérer la série actuelle
      const currentSeries = await this.getEventSeriesById(seriesId);
      if (!currentSeries) {
        throw new Error('Series not found');
      }

      const organizerId = updates.organizerId || currentSeries.organizerId;

      // 1. Détecter si les règles de récurrence ont changé
      const recurrenceChanged = this._detectRecurrenceChanges(currentSeries, updates);
      console.log('🔍 [Supabase] Changement de récurrence détecté:', recurrenceChanged);

      // 2. Mettre à jour la série (event_series table)
      const updateData: DbEventSeriesUpdate = {
        updated_at: new Date().toISOString(),
      };

      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.location !== undefined) updateData.location = updates.location;
      if (updates.locationName !== undefined) updateData.location_name = updates.locationName;
      if (updates.latitude !== undefined) updateData.latitude = updates.latitude;
      if (updates.longitude !== undefined) updateData.longitude = updates.longitude;
      if (updates.timezone !== undefined) updateData.timezone = updates.timezone; // 🌍 Timezone IANA
      if (updates.tags !== undefined) updateData.tags = updates.tags;
      if (updates.image !== undefined) updateData.image = updates.image;
      if (updates.prices !== undefined) updateData.prices = updates.prices as unknown as Json;
      if (updates.ticketingUrl !== undefined) updateData.ticketing_url = updates.ticketingUrl;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.frequency !== undefined) updateData.frequency = updates.frequency;
      if (updates.startDate !== undefined) updateData.start_date = updates.startDate;
      if (updates.startTime !== undefined) updateData.start_time = updates.startTime;
      if (updates.endTime !== undefined) updateData.end_time = updates.endTime;
      if (updates.endType !== undefined) updateData.end_type = updates.endType;
      if (updates.endDate !== undefined) updateData.end_date = updates.endDate || null; // Chaîne vide → null pour PostgreSQL
      if (updates.occurrences !== undefined) updateData.occurrences = updates.occurrences;
      if (updates.weekDays !== undefined) updateData.week_days = updates.weekDays;

      // day_schedules is not yet in generated types
      const finalUpdateData: any = { ...updateData };
      if (updates.daySchedules !== undefined) {
        finalUpdateData.day_schedules = updates.daySchedules;
      }

      const { error: updateError } = await supabase
        .from('event_series')
        .update(finalUpdateData)
        .eq('id', seriesId);

      if (updateError) throw updateError;

      // 3. Si récurrence a changé, gérer les occurrences
      let recurrenceResult: RecurrenceChangeResult | undefined;
      if (recurrenceChanged && !options.skipRecurrenceSync) {
        const updatedSeries: EventSeries = { ...currentSeries, ...updates };
        recurrenceResult = await this._handleRecurrenceChange(seriesId, updatedSeries);
        console.log('📊 [Supabase] Résultat changement récurrence:', recurrenceResult);
      }

      // 3.5. Si l'heure de début/fin, timezone ou daySchedules a changé, mettre à jour le datetime des occurrences existantes
      const daySchedulesChanged = updates.daySchedules !== undefined &&
        JSON.stringify(updates.daySchedules) !== JSON.stringify(currentSeries.daySchedules);
      const timeChanged = (updates.startTime !== undefined && updates.startTime !== currentSeries.startTime) ||
                          (updates.endTime !== undefined && updates.endTime !== currentSeries.endTime) ||
                          (updates.timezone !== undefined && updates.timezone !== currentSeries.timezone) ||
                          daySchedulesChanged;

      // 🐢 FIX (robustesse réseau faible - item 5c): la boucle ci-dessous avalait chaque
      // erreur individuelle (console.error + on continue) sans jamais le faire savoir au
      // caller. Résultat: certaines occurrences gardaient l'ancienne heure, d'autres la
      // nouvelle, et la fonction retournait quand même un "succès". On agrège maintenant
      // les échecs et on les remonte (throw) à la fin de updateEventSeries si au moins
      // une mise à jour a raté, pour que le caller (handleRecurringEvent.ts) puisse
      // informer l'utilisateur au lieu d'afficher un faux toast de succès.
      const failedOccurrenceTimeUpdates: string[] = [];
      let occurrencesFetchFailed = false;

      if (timeChanged) {
        const newStartTime = updates.startTime ?? currentSeries.startTime ?? '00:00';
        const newEndTime = updates.endTime ?? currentSeries.endTime;
        const newTimezone = updates.timezone ?? currentSeries.timezone ?? 'Asia/Colombo';
        const newDaySchedules = updates.daySchedules !== undefined ? updates.daySchedules : currentSeries.daySchedules;

        console.log('⏰ [Supabase] Mise à jour des datetime des occurrences:', { newStartTime, newEndTime, newTimezone, hasDaySchedules: !!newDaySchedules });

        // Récupérer toutes les occurrences non-exceptions
        const { data: occurrences, error: fetchError } = await supabase
          .from('events')
          .select('id, datetime')
          .eq('series_id', seriesId)
          .eq('is_exception', false);

        if (fetchError) {
          console.error('❌ [Supabase] Erreur récupération occurrences:', fetchError);
          occurrencesFetchFailed = true;
        } else if (occurrences && occurrences.length > 0) {
          console.log(`⏰ [Supabase] ${occurrences.length} occurrences à mettre à jour`);

          // Mettre à jour chaque occurrence avec le nouveau datetime
          for (const occ of occurrences) {
            // Skip si pas de datetime
            if (!occ.datetime) continue;

            // Extraire la date de l'ancien datetime (en timezone de l'événement)
            const oldDatetime = new Date(occ.datetime);
            // Formater la date en YYYY-MM-DD (UTC car c'est stocké en UTC)
            const dateStr = oldDatetime.toISOString().split('T')[0];

            // Use per-day schedule if available
            const dayName = getDayNameFromDate(oldDatetime);
            const daySchedule = newDaySchedules?.[dayName];
            const occStartTime = daySchedule?.start || newStartTime;
            const occEndTime = daySchedule?.end || newEndTime;

            // Calculer le nouveau datetime UTC avec la nouvelle heure et timezone
            const newDatetimeUTC = localToUTC(dateStr, occStartTime, newTimezone);
            const newEndTimeISO = localEndTimeToUTC(dateStr, occStartTime, occEndTime, newTimezone, newDatetimeUTC);

            const { error: updateOccError } = await supabase
              .from('events')
              .update({
                datetime: newDatetimeUTC,
                end_time: newEndTimeISO || null,
                timezone: newTimezone
              })
              .eq('id', occ.id);

            if (updateOccError) {
              console.error(`❌ [Supabase] Erreur mise à jour occurrence ${occ.id}:`, updateOccError);
              failedOccurrenceTimeUpdates.push(occ.id);
            }
          }

          if (failedOccurrenceTimeUpdates.length > 0) {
            console.error(`❌ [Supabase] ${failedOccurrenceTimeUpdates.length}/${occurrences.length} occurrence(s) n'ont PAS pu être mises à jour avec la nouvelle heure`);
          } else {
            console.log('✅ [Supabase] Datetime des occurrences mis à jour');
          }
        }
      }

      // 4. Mettre à jour le contenu des événements non-exception restants
      const eventUpdates: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };

      if (updates.title !== undefined) eventUpdates.title = updates.title;
      if (updates.description !== undefined) eventUpdates.description = updates.description;
      if (updates.location !== undefined) eventUpdates.location = updates.location;
      if (updates.locationName !== undefined) eventUpdates.location_name = updates.locationName;
      if (updates.latitude !== undefined) eventUpdates.latitude = updates.latitude;
      if (updates.longitude !== undefined) eventUpdates.longitude = updates.longitude;
      if (updates.timezone !== undefined) eventUpdates.timezone = updates.timezone; // 🌍 Timezone IANA
      if (updates.tags !== undefined) eventUpdates.tags = updates.tags;
      if (updates.image !== undefined) eventUpdates.image = updates.image;
      if (updates.prices !== undefined) eventUpdates.prices = updates.prices;
      if (updates.ticketingUrl !== undefined) eventUpdates.ticketing_url = updates.ticketingUrl;
      if (updates.status !== undefined) eventUpdates.status = updates.status;
      // Mettre à jour les métadonnées de série sur les événements
      if (updates.frequency !== undefined) eventUpdates.series_frequency = updates.frequency;
      if (updates.endType !== undefined) eventUpdates.series_end_type = updates.endType;
      if (updates.endDate !== undefined) eventUpdates.series_end_date = updates.endDate || null; // Chaîne vide → null pour PostgreSQL
      if (updates.occurrences !== undefined) eventUpdates.series_occurrences = updates.occurrences;
      if (updates.weekDays !== undefined) eventUpdates.week_days = updates.weekDays;

      const { error: eventsError } = await supabase
        .from('events')
        .update(eventUpdates)
        .eq('series_id', seriesId)
        .eq('is_exception', false);

      if (eventsError) throw eventsError;

      console.log('✅ [Supabase] Série et événements mis à jour');

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      if (organizerId) {
        await smartListenerService.forceRefreshOrganizerEvents(organizerId);
        await smartListenerService.forceRefreshSeries(organizerId);
      }

      // ⚠️ Échec partiel sur la mise à jour des horaires: on a fait tout le reste
      // (contenu, refresh UI) au mieux, mais on ne renvoie PAS un succès silencieux —
      // le caller doit savoir que certaines occurrences gardent l'ancien horaire.
      if (occurrencesFetchFailed) {
        throw new Error('Failed to fetch series occurrences while updating their time - occurrence times may be inconsistent');
      }
      if (failedOccurrenceTimeUpdates.length > 0) {
        throw new Error(`${failedOccurrenceTimeUpdates.length} occurrence(s) could not be updated with the new time (partial failure, some occurrences may keep the old time)`);
      }

      return recurrenceResult;
    } catch (error) {
      console.error('❌ [Supabase] Erreur mise à jour série:', error);
      throw error;
    }
  },

  /**
   * Met à jour un événement individuel (crée une exception)
   */
  async updateSingleEvent(eventId: string, updates: Partial<any>): Promise<void> {
    try {
      console.log('📝 [Supabase] Mise à jour événement individuel:', eventId, updates);

      if (!eventId) {
        throw new Error('eventId is required for update');
      }

      // Récupérer l'événement actuel
      const { data: event, error: fetchError } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();

      if (fetchError) throw fetchError;
      if (!event) throw new Error('Event not found');

      // Mapping explicite camelCase → snake_case pour Supabase
      const dbUpdates: Record<string, unknown> = {
        is_exception: true,
        updated_at: new Date().toISOString(),
      };

      // Mapper chaque champ individuellement
      if (updates.title !== undefined) dbUpdates.title = updates.title;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.datetime !== undefined) {
        dbUpdates.datetime = updates.datetime instanceof Date
          ? updates.datetime.toISOString()
          : updates.datetime;
      }
      if (updates.endTime !== undefined) dbUpdates.end_time = updates.endTime;
      if (updates.location !== undefined) dbUpdates.location = updates.location;
      if (updates.locationName !== undefined) dbUpdates.location_name = updates.locationName;
      if (updates.latitude !== undefined) dbUpdates.latitude = updates.latitude;
      if (updates.longitude !== undefined) dbUpdates.longitude = updates.longitude;
      if (updates.geohash !== undefined) dbUpdates.geohash = updates.geohash;
      if (updates.tags !== undefined) dbUpdates.tags = updates.tags;
      if (updates.image !== undefined) dbUpdates.image = updates.image;
      if (updates.prices !== undefined) dbUpdates.prices = updates.prices;
      if (updates.ticketingUrl !== undefined) dbUpdates.ticketing_url = updates.ticketingUrl;
      if (updates.status !== undefined) dbUpdates.status = updates.status;

      console.log('📤 [Supabase] Données mappées pour update:', dbUpdates);

      const { error } = await supabase
        .from('events')
        .update(dbUpdates)
        .eq('id', eventId);

      if (error) {
        console.error('❌ [Supabase] Erreur Supabase:', error);
        throw new Error(`Failed to update event: ${error.message}`);
      }

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      if (event.organizer_id) {
        await smartListenerService.forceRefreshOrganizerEvents(event.organizer_id);
      }

      console.log('✅ [Supabase] Événement individuel mis à jour:', eventId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur mise à jour événement:', error);
      throw error;
    }
  },

  /**
   * Récupère une série par ID
   */
  async getEventSeriesById(seriesId: string): Promise<EventSeries | null> {
    try {
      console.log('🔍 [Supabase] Chargement série:', seriesId);

      // 🔧 DEBUG: Ajouter un timeout pour détecter les requêtes qui bloquent
      const queryPromise = supabase
        .from('event_series')
        .select('*')
        .eq('id', seriesId)
        .single();

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => {
          console.error('⏰ [Supabase] TIMEOUT getEventSeriesById après 8s pour série:', seriesId);
          reject(new Error(`Query timeout après 8s pour série ${seriesId}`));
        }, 8000)
      );

      const { data, error } = await Promise.race([queryPromise, timeoutPromise]);

      console.log('📦 [Supabase] Réponse getEventSeriesById:', {
        hasData: !!data,
        error: error?.message,
        errorCode: error?.code,
        rawData: data ? {
          id: data.id,
          start_date: data.start_date,
          start_time: data.start_time,
          end_date: data.end_date,
          end_type: data.end_type,
          frequency: data.frequency
        } : null
      });

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      if (!data) return null;

      const series = dbSeriesToEventSeries(data);
      console.log('✅ [Supabase] Série chargée:', seriesId, {
        startDate: series.startDate,
        startTime: series.startTime,
        endDate: series.endDate,
        endType: series.endType,
        frequency: series.frequency
      });
      return series;
    } catch (error) {
      console.error('❌ [Supabase] Erreur chargement série:', error);
      throw error;
    }
  },

  /**
   * Récupère toutes les séries d'un organisateur
   */
  async getSeriesByOrganizerId(organizerId: string, status?: 'active' | 'draft'): Promise<EventSeries[]> {
    try {
      console.log('🔍 [Supabase] Chargement séries organisateur:', organizerId);

      let query = supabase
        .from('event_series')
        .select('*')
        .eq('organizer_id', organizerId);

      if (status) {
        query = query.eq('status', status);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      const series = (data ?? []).map(dbSeriesToEventSeries);
      console.log(`✅ [Supabase] ${series.length} séries chargées`);
      return series;
    } catch (error) {
      console.error('❌ [Supabase] Erreur chargement séries:', error);
      throw error;
    }
  },

  /**
   * Récupère une série par son ID (pour les touristes/affichage public)
   */
  async getSeriesById(seriesId: string): Promise<EventSeries | null> {
    try {
      console.log('🔍 [Supabase] Chargement série par ID:', seriesId);

      const { data, error } = await supabase
        .from('event_series')
        .select('*')
        .eq('id', seriesId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No rows returned
          console.log('📭 [Supabase] Série non trouvée:', seriesId);
          return null;
        }
        throw error;
      }

      if (!data) return null;

      const series = dbSeriesToEventSeries(data);
      console.log('✅ [Supabase] Série chargée:', series.title);
      return series;
    } catch (error) {
      console.error('❌ [Supabase] Erreur chargement série:', error);
      return null;
    }
  },

  /**
   * Récupère plusieurs séries par leurs IDs (batch - pour la HomePage touriste)
   */
  async getSeriesByIds(seriesIds: string[]): Promise<EventSeries[]> {
    if (seriesIds.length === 0) return [];
    try {
      const { data, error } = await supabase
        .from('event_series')
        .select('*')
        .in('id', seriesIds);

      if (error) throw error;
      return (data ?? []).map(row => dbSeriesToEventSeries(row));
    } catch (error) {
      console.error('❌ [Supabase] Erreur batch chargement séries:', error);
      return [];
    }
  },

  /**
   * Supprime une série et tous ses événements
   */
  async deleteEventSeries(seriesId: string): Promise<void> {
    try {
      console.log('🗑️ [Supabase] Suppression série:', seriesId);

      // Récupérer l'organizerId AVANT suppression pour le refresh
      const { data: series } = await supabase
        .from('event_series')
        .select('organizer_id')
        .eq('id', seriesId)
        .single();

      const organizerId = series?.organizer_id;

      // Les événements seront supprimés automatiquement grâce à ON DELETE CASCADE
      const { error } = await supabase
        .from('event_series')
        .delete()
        .eq('id', seriesId);

      if (error) throw error;

      // 🔄 FORCE REFRESH: Notifier les consumers pour mise à jour immédiate de l'UI
      if (organizerId) {
        await smartListenerService.forceRefreshOrganizerEvents(organizerId);
        await smartListenerService.forceRefreshSeries(organizerId);
      }

      console.log('✅ [Supabase] Série supprimée');
    } catch (error) {
      console.error('❌ [Supabase] Erreur suppression série:', error);
      throw error;
    }
  },

  /**
   * Détecte et nettoie les doublons dans une série
   */
  async detectAndCleanDuplicates(seriesId: string): Promise<{ duplicates: number; cleaned: number }> {
    try {
      console.log('🔍 [Supabase] Détection doublons pour série:', seriesId);

      const { data: events, error } = await supabase
        .from('events')
        .select('id, datetime, is_exception')
        .eq('series_id', seriesId);

      if (error) throw error;
      if (!events || events.length === 0) {
        return { duplicates: 0, cleaned: 0 };
      }

      // Grouper par datetime
      const eventsByDateTime = new Map<string, typeof events>();
      events.forEach(event => {
        const dateKey = event.datetime ?? '';
        if (!eventsByDateTime.has(dateKey)) {
          eventsByDateTime.set(dateKey, []);
        }
        eventsByDateTime.get(dateKey)!.push(event);
      });

      // Identifier les doublons
      const toDelete: string[] = [];
      let duplicatesCount = 0;

      eventsByDateTime.forEach((evts) => {
        if (evts.length > 1) {
          duplicatesCount += evts.length - 1;
          // Garder le premier non-exception
          const toKeep = evts.find(e => !e.is_exception) || evts[0];
          evts.forEach(e => {
            if (e.id !== toKeep.id) {
              toDelete.push(e.id);
            }
          });
        }
      });

      // Supprimer les doublons
      if (toDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('events')
          .delete()
          .in('id', toDelete);

        if (deleteError) throw deleteError;
      }

      console.log(`✅ [Supabase] ${toDelete.length} doublons supprimés`);
      return { duplicates: duplicatesCount, cleaned: toDelete.length };
    } catch (error) {
      console.error('❌ [Supabase] Erreur nettoyage doublons:', error);
      throw error;
    }
  },

  /**
   * Détecte si les règles de récurrence ont changé
   */
  _detectRecurrenceChanges(
    currentSeries: EventSeries,
    updates: Partial<EventSeries>
  ): boolean {
    // Vérifier chaque champ de récurrence
    if (updates.frequency !== undefined && updates.frequency !== currentSeries.frequency) {
      return true;
    }
    if (updates.startDate !== undefined && updates.startDate !== currentSeries.startDate) {
      return true;
    }
    if (updates.endDate !== undefined && updates.endDate !== currentSeries.endDate) {
      return true;
    }
    if (updates.occurrences !== undefined && updates.occurrences !== currentSeries.occurrences) {
      return true;
    }
    if (updates.endType !== undefined && updates.endType !== currentSeries.endType) {
      return true;
    }
    if (updates.weekDays !== undefined) {
      const currentDays = JSON.stringify((currentSeries.weekDays || []).sort());
      const newDays = JSON.stringify((updates.weekDays || []).sort());
      if (currentDays !== newDays) {
        return true;
      }
    }
    if (updates.startTime !== undefined && updates.startTime !== currentSeries.startTime) {
      return true;
    }
    // 🌍 Le changement de timezone nécessite une régénération des occurrences (conversion UTC)
    if (updates.timezone !== undefined && updates.timezone !== currentSeries.timezone) {
      return true;
    }
    // daySchedules change = recurrence change (times differ per day)
    if (updates.daySchedules !== undefined) {
      const currentDS = JSON.stringify(currentSeries.daySchedules || null);
      const newDS = JSON.stringify(updates.daySchedules || null);
      if (currentDS !== newDS) return true;
    }
    return false;
  },

  /**
   * Prévisualise les changements de récurrence sans les appliquer
   *
   * LOGIQUE IMPORTANTE:
   * - On supprime les occurrences HORS de la nouvelle plage de dates (raccourcissement)
   * - On supprime les occurrences qui NE MATCHENT PAS le nouveau pattern (changement weekDays/fréquence)
   * - On AJOUTE les dates manquantes selon le nouveau pattern
   * - Les exceptions (is_exception=true) sont signalées mais NON supprimées automatiquement
   */
  async previewRecurrenceChange(
    seriesId: string,
    updates: Partial<EventSeries>
  ): Promise<RecurrenceChangePreview> {
    console.log('🔍 [Supabase] Prévisualisation changements récurrence:', seriesId);

    // Récupérer la série actuelle
    const currentSeries = await this.getEventSeriesById(seriesId);
    if (!currentSeries) {
      throw new Error('Series not found');
    }

    // Fusionner avec les updates pour créer la série mise à jour
    const updatedSeries: EventSeries = { ...currentSeries, ...updates };

    // Générer les nouvelles dates attendues selon le nouveau pattern
    const newDates = this._generateEventDates(updatedSeries);

    // Créer un Set des dates du nouveau pattern pour comparaison rapide
    const newDatesSet = new Set(
      newDates.map(d => d.toISOString().split('T')[0])
    );

    // Calculer la nouvelle plage de dates (pour déterminer ce qui est "hors plage")
    const newStartDate = updatedSeries.startDate
      ? new Date(updatedSeries.startDate + 'T00:00:00')
      : null;

    let newEndDate: Date | null = null;
    if (updatedSeries.endType === 'date' && updatedSeries.endDate) {
      newEndDate = new Date(updatedSeries.endDate + 'T23:59:59');
    } else if (newDates.length > 0) {
      // Si endType === 'occurrences', la dernière date générée est la fin
      newEndDate = new Date(newDates[newDates.length - 1]);
      newEndDate.setHours(23, 59, 59);
    }

    console.log('📅 [Supabase] Nouvelle plage:', {
      start: newStartDate?.toISOString().split('T')[0],
      end: newEndDate?.toISOString().split('T')[0],
      patternDates: newDates.length
    });

    // Récupérer les occurrences existantes
    const { data: existingEvents, error } = await supabase
      .from('events')
      .select('id, datetime, is_exception')
      .eq('series_id', seriesId);

    if (error) throw error;

    // Identifier les occurrences à supprimer
    // Cas A: Hors plage de dates (raccourcissement)
    // Cas B: Ne matche pas le nouveau pattern (changement weekDays/fréquence)
    const toDelete: Array<{ id: string; datetime: string }> = [];
    const exceptionsAffected: Array<{ id: string; datetime: string }> = [];
    let unchanged = 0;

    (existingEvents || []).forEach(event => {
      if (!event.datetime) return;

      const eventDateTime = new Date(event.datetime);
      const eventDateStr = eventDateTime.toISOString().split('T')[0];

      // Cas A: Vérifier si l'événement est HORS de la nouvelle plage de dates
      const isBeforeStart = newStartDate && eventDateTime < newStartDate;
      const isAfterEnd = newEndDate && eventDateTime > newEndDate;
      const isOutsideRange = isBeforeStart || isAfterEnd;

      // Cas B: Vérifier si l'événement NE MATCHE PAS le nouveau pattern
      const doesNotMatchPattern = !newDatesSet.has(eventDateStr);

      // Supprimer si hors plage OU ne matche pas le pattern
      if (isOutsideRange || doesNotMatchPattern) {
        if (event.is_exception) {
          // Les exceptions sont signalées pour confirmation utilisateur
          exceptionsAffected.push({ id: event.id, datetime: event.datetime });
        } else {
          toDelete.push({ id: event.id, datetime: event.datetime });
        }
      } else {
        // Événement dans la plage ET matche le pattern → on le garde
        unchanged++;
      }
    });

    // Identifier les dates à créer (dates du nouveau pattern qui n'ont pas d'événement existant)
    const existingDatesSet = new Set(
      (existingEvents || []).map(e =>
        e.datetime ? new Date(e.datetime).toISOString().split('T')[0] : ''
      )
    );

    const toCreate = newDates.filter(
      d => !existingDatesSet.has(d.toISOString().split('T')[0])
    );

    console.log('📊 [Supabase] Prévisualisation:', {
      toDelete: toDelete.length,
      toCreate: toCreate.length,
      unchanged,
      exceptionsAffected: exceptionsAffected.length
    });

    return {
      toDelete,
      toCreate,
      unchanged,
      exceptionsAffected
    };
  },

  /**
   * Applique les changements de récurrence (supprime/crée des occurrences)
   */
  async _handleRecurrenceChange(
    seriesId: string,
    updatedSeries: EventSeries,
    preview?: RecurrenceChangePreview
  ): Promise<RecurrenceChangeResult> {
    console.log('🔄 [Supabase] Application changements récurrence:', seriesId);

    // Récupérer ou calculer le preview
    const changePreview = preview || await this.previewRecurrenceChange(seriesId, updatedSeries);

    // 1. Supprimer les occurrences en trop (non-exceptions seulement)
    if (changePreview.toDelete.length > 0) {
      const idsToDelete = changePreview.toDelete.map(e => e.id);
      console.log(`🗑️ [Supabase] Suppression de ${idsToDelete.length} occurrences`);

      const { error: deleteError } = await supabase
        .from('events')
        .delete()
        .in('id', idsToDelete);

      if (deleteError) {
        console.error('❌ [Supabase] Erreur suppression:', deleteError);
        throw deleteError;
      }
    }

    // 2. Créer les nouvelles occurrences si nécessaire
    let created = 0;
    if (changePreview.toCreate.length > 0) {
      console.log(`📅 [Supabase] Création de ${changePreview.toCreate.length} nouvelles occurrences`);

      const timezone = updatedSeries.timezone || 'Asia/Colombo';
      const events: DbEventInsert[] = changePreview.toCreate.map(date => {
        // Extraire la date au format YYYY-MM-DD pour la conversion UTC
        const dateStr = date.toISOString().split('T')[0];
        // Use per-day schedule if available, otherwise fall back to uniform time
        const dayName = getDayNameFromDate(date);
        const daySchedule = updatedSeries.daySchedules?.[dayName];
        const startTime = daySchedule?.start || updatedSeries.startTime || '00:00';
        const endTime = daySchedule?.end || updatedSeries.endTime;
        // Convertir l'heure locale de l'événement en UTC avec le bon timezone
        const datetimeUTC = localToUTC(dateStr, startTime, timezone);
        const endTimeISO = localEndTimeToUTC(dateStr, startTime, endTime, timezone, datetimeUTC);

        return {
          organizer_id: updatedSeries.organizerId,
          series_id: seriesId,
          title: updatedSeries.title,
          description: updatedSeries.description || null,
          datetime: datetimeUTC,
          end_time: endTimeISO || null,
          timezone: timezone, // 🌍 Timezone de l'événement
          location: updatedSeries.location || null,
          location_name: updatedSeries.locationName || null,
          latitude: updatedSeries.latitude ?? null,
          longitude: updatedSeries.longitude ?? null,
          tags: updatedSeries.tags ?? [],
          image: updatedSeries.image || null,
          prices: (updatedSeries.prices ?? []) as unknown as Json,
          ticketing_url: updatedSeries.ticketingUrl || null,
          status: updatedSeries.status || 'active',
          is_exception: false,
          week_days: updatedSeries.weekDays ?? [],
          series_frequency: updatedSeries.frequency || null,
          series_end_type: updatedSeries.endType || null,
          series_end_date: updatedSeries.endDate || null,
          series_occurrences: updatedSeries.occurrences ?? null,
        };
      });

      // Insérer par lots de 50
      const batchSize = 50;
      for (let i = 0; i < events.length; i += batchSize) {
        const batch = events.slice(i, i + batchSize);
        const { error } = await supabase.from('events').insert(batch);
        if (error) {
          console.error('❌ [Supabase] Erreur création batch:', error);
          throw error;
        }
        created += batch.length;
      }
    }

    console.log('✅ [Supabase] Changements récurrence appliqués:', {
      deleted: changePreview.toDelete.length,
      created,
      exceptionsPreserved: changePreview.exceptionsAffected.length
    });

    return {
      success: true,
      deleted: changePreview.toDelete.length,
      created,
      exceptionsPreserved: changePreview.exceptionsAffected.length
    };
  },

  /**
   * Annule une série et toutes ses occurrences non-exceptions
   * Les exceptions (is_exception=true) ne sont PAS modifiées
   */
  async cancelEventSeries(seriesId: string, reason?: string): Promise<void> {
    try {
      console.log('🚫 [Supabase] Annulation série:', seriesId);

      // Récupérer l'organizerId pour le refresh
      const series = await this.getEventSeriesById(seriesId);
      if (!series) {
        throw new Error('Series not found');
      }

      const cancelledAt = new Date().toISOString();

      // 1. Mettre à jour la série
      const { error: seriesError } = await supabase
        .from('event_series')
        .update({
          status: 'cancelled',
          cancelled_at: cancelledAt,
          cancellation_reason: reason || null,
          updated_at: cancelledAt
        })
        .eq('id', seriesId);

      if (seriesError) {
        console.error('❌ [Supabase] Erreur annulation série:', seriesError);
        throw seriesError;
      }

      // 2. Mettre à jour toutes les occurrences NON-EXCEPTIONS
      const { error: eventsError } = await supabase
        .from('events')
        .update({
          status: 'cancelled',
          cancelled_at: cancelledAt,
          cancellation_reason: reason || null,
          updated_at: cancelledAt
        })
        .eq('series_id', seriesId)
        .eq('is_exception', false);

      if (eventsError) {
        console.error('❌ [Supabase] Erreur annulation occurrences:', eventsError);
        throw eventsError;
      }

      // 3. Forcer le refresh de l'UI
      await smartListenerService.forceRefreshOrganizerEvents(series.organizerId);
      await smartListenerService.forceRefreshSeries(series.organizerId);

      console.log('✅ [Supabase] Série annulée:', seriesId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur annulation série:', error);
      throw error;
    }
  },

  /**
   * Restaure une série annulée et toutes ses occurrences non-exceptions
   * Les exceptions (is_exception=true) ne sont PAS modifiées
   */
  async restoreEventSeries(seriesId: string): Promise<void> {
    try {
      console.log('🔄 [Supabase] Restauration série:', seriesId);

      // Récupérer l'organizerId pour le refresh
      const series = await this.getEventSeriesById(seriesId);
      if (!series) {
        throw new Error('Series not found');
      }

      const updatedAt = new Date().toISOString();

      // 1. Mettre à jour la série
      const { error: seriesError } = await supabase
        .from('event_series')
        .update({
          status: 'active',
          cancelled_at: null,
          cancellation_reason: null,
          updated_at: updatedAt
        })
        .eq('id', seriesId);

      if (seriesError) {
        console.error('❌ [Supabase] Erreur restauration série:', seriesError);
        throw seriesError;
      }

      // 2. Mettre à jour toutes les occurrences NON-EXCEPTIONS qui sont cancelled
      const { error: eventsError } = await supabase
        .from('events')
        .update({
          status: 'active',
          cancelled_at: null,
          cancellation_reason: null,
          updated_at: updatedAt
        })
        .eq('series_id', seriesId)
        .eq('is_exception', false)
        .eq('status', 'cancelled'); // Seulement les cancelled

      if (eventsError) {
        console.error('❌ [Supabase] Erreur restauration occurrences:', eventsError);
        throw eventsError;
      }

      // 3. Forcer le refresh de l'UI
      await smartListenerService.forceRefreshOrganizerEvents(series.organizerId);
      await smartListenerService.forceRefreshSeries(series.organizerId);

      console.log('✅ [Supabase] Série restaurée:', seriesId);
    } catch (error) {
      console.error('❌ [Supabase] Erreur restauration série:', error);
      throw error;
    }
  },

  /**
   * Add an exceptional occurrence to a series
   * Used for adding one-off dates outside the regular pattern
   */
  async addExceptionalOccurrence(
    seriesId: string,
    data: { datetime: Date; endTime?: string }
  ): Promise<string> {
    console.log('➕ [Supabase] Ajout occurrence exceptionnelle:', seriesId);

    // Get the series to copy data from
    const series = await this.getEventSeriesById(seriesId);
    if (!series) {
      throw new Error('Series not found');
    }

    // Create the exceptional event
    const eventData: DbEventInsert = {
      organizer_id: series.organizerId,
      series_id: seriesId,
      title: series.title,
      description: series.description || null,
      datetime: data.datetime.toISOString(),
      end_time: data.endTime || null,
      timezone: series.timezone || 'Asia/Colombo', // 🌍 Timezone de la série
      location: series.location || null,
      location_name: series.locationName || null,
      latitude: series.latitude ?? null,
      longitude: series.longitude ?? null,
      tags: series.tags ?? [],
      image: series.image || null,
      prices: (series.prices ?? []) as unknown as Json,
      ticketing_url: series.ticketingUrl || null,
      status: series.status || 'active',
      is_exception: true, // Mark as exception
      week_days: series.weekDays ?? [],
      series_frequency: series.frequency || null,
      series_end_type: series.endType || null,
      series_end_date: series.endDate || null,
      series_occurrences: series.occurrences ?? null,
    };

    const { data: insertedEvent, error } = await supabase
      .from('events')
      .insert(eventData)
      .select('id')
      .single();

    if (error) {
      console.error('❌ [Supabase] Erreur ajout occurrence:', error);
      throw error;
    }

    console.log('✅ [Supabase] Occurrence exceptionnelle ajoutée:', insertedEvent.id);
    return insertedEvent.id;
  },
};
